/**
 * ReadinessBadge — shared "Ready" / "Missing: ..." chip for item-add surfaces.
 *
 * Wraps itemReadiness.ts's computeItemReadiness()/getBlockingReadinessIssues()
 * (the same functions ItemPoolEditor.tsx's inline "Add Items" panel already
 * uses) so every surface that lets a user add items to a pool — the filter
 * modal, the add-from-collection modal, and (eventually) the existing panels —
 * renders the same readiness signal instead of each maintaining its own
 * ad hoc chip.
 */

import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { Attribute } from '../../../../types/prioritization';
import type { Candidate } from '../../../../types/prioritization/lifecycle';
import { getBlockingReadinessIssues } from '../../../../lib/itemReadiness';

export interface ReadinessBadgeProps {
  item: Candidate;
  /** Required attributes to check against. Defaults to the workspace's required attributes if omitted. */
  requiredAttributes?: Array<Pick<Attribute, 'key' | 'label' | 'required'>>;
  className?: string;
}

export function ReadinessBadge({ item, requiredAttributes, className = '' }: ReadinessBadgeProps) {
  // Not a React hook call (getBlockingReadinessIssues calls no hooks internally) —
  // kept un-exported here so this file exports only the component, per
  // react-refresh/only-export-components. Callers that need the same
  // ready/blockingIssues computation to gate an action should call
  // getBlockingReadinessIssues() from lib/itemReadiness.ts directly.
  const blockingIssues = getBlockingReadinessIssues(item, requiredAttributes);
  const ready = blockingIssues.length === 0;

  if (ready) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/30 dark:text-[var(--text-ter)] ${className}`}
      >
        <CheckCircle2 className="h-3 w-3" />
        Ready
      </span>
    );
  }

  const summary = blockingIssues.slice(0, 2).map((i) => i.displayText).join(', ');
  const overflow = blockingIssues.length > 2 ? ` +${blockingIssues.length - 2} more` : '';

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/30 dark:text-[var(--mustard)] ${className}`}
      title={blockingIssues.map((i) => i.displayText).join(', ')}
    >
      <AlertTriangle className="h-3 w-3" />
      {blockingIssues.length} missing
      {summary && <span className="hidden sm:inline">: {summary}{overflow}</span>}
    </span>
  );
}
